import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { trackContractSigned, trackOnboardingCompleted } from "./analyticsTracker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Icons = {
  Check: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  File: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>
};

export const ContractPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed || !signature.trim()) return alert("Please agree to the terms and type your signature.");
    setLoading(true);
    try {
      await axios.post(`${API}/leads/${id}/contract`, { signature });
      trackContractSigned(id);
      setSubmitted(true);
      setTimeout(() => navigate(`/onboarding/${id}`), 3000);
    } catch (err) {
      alert("Failed to submit contract.");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icons.Check />
          </div>
          <h2 className="text-2xl font-bold mb-2">Contract Signed!</h2>
          <p className="text-white/60 mb-6">Redirecting to onboarding questionnaire...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black mb-2">Service <span className="text-red-500">Agreement</span></h1>
          <p className="text-white/60">Please review and sign to proceed.</p>
        </div>

        <div className="glass-card rounded-2xl p-6 md:p-8 mb-8 border border-white/10">
          <div className="prose prose-invert max-w-none text-sm text-white/80 h-96 overflow-y-auto pr-4 mb-6" style={{ scrollbarWidth: 'thin' }}>
            <h3>1. Services Provided</h3>
            <p>ClickCutGo ("Provider") agrees to provide video production and editing services to the Client for their event. The final deliverables will match the package selected.</p>
            <h3>2. Payment Terms</h3>
            <p>A 50% advance payment is required to lock the slot. The remaining 50% must be paid prior to the delivery of the final assets. Advance payments are strictly non-refundable.</p>
            <h3>3. Intellectual Property</h3>
            <p>The Provider retains the right to use the footage for promotional portfolio purposes unless explicitly requested otherwise via a Non-Disclosure Agreement (NDA).</p>
            <h3>4. Client Responsibilities</h3>
            <p>The Client must provide necessary venue access, clearances, and branding assets requested in the onboarding questionnaire.</p>
            <h3>5. Delivery Timeline</h3>
            <p>Initial reels will be delivered within the promised timeline (same-day or 24-48 hours depending on the package).</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 border-t border-white/10 pt-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1 w-5 h-5 rounded bg-white/5 border border-white/20 text-red-500 focus:ring-red-500" />
              <span className="text-sm text-white/70">I have read and agree to the Service Agreement above. I understand that the 50% advance payment is non-refundable.</span>
            </label>

            <div>
              <label className="block text-white/60 text-sm mb-2">Type your full legal name to sign</label>
              <input type="text" value={signature} onChange={e => setSignature(e.target.value)} required placeholder="John Doe" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500" />
            </div>

            <button type="submit" disabled={!agreed || !signature || loading} className="btn-primary w-full py-4 text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? "Signing..." : "Sign Agreement"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export const OnboardingPage = () => {
  const { id } = useParams();
  const [form, setForm] = useState({ goals: "", assets_link: "", inspiration_link: "", deadline_note: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads/${id}/onboarding`, form);
      trackOnboardingCompleted(id);
      setSubmitted(true);
    } catch (err) {
      alert("Failed to submit onboarding details.");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/20 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icons.Check />
          </div>
          <h2 className="text-3xl font-black mb-2">You're All Set!</h2>
          <p className="text-white/60 mb-6">We've received your project details. Our team is reviewing everything and will start working immediately.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black mb-2">Project <span className="text-red-500">Brief</span></h1>
          <p className="text-white/60">Help us understand your vision.</p>
        </div>

        <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-6 md:p-8 space-y-6 border border-white/10">
          <div>
            <label className="block text-white/80 font-bold mb-2">What is the primary goal of these videos?</label>
            <p className="text-white/40 text-xs mb-2">e.g., Brand Awareness, Sales, Event Highlight, Influencer Collab</p>
            <textarea value={form.goals} onChange={e => setForm({...form, goals: e.target.value})} required className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 h-24 resize-none" />
          </div>

          <div>
            <label className="block text-white/80 font-bold mb-2">Brand Assets Link</label>
            <p className="text-white/40 text-xs mb-2">Paste a Google Drive or Dropbox link with your Logos, Fonts, and Brand Colors.</p>
            <input type="url" value={form.assets_link} onChange={e => setForm({...form, assets_link: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500" placeholder="https://drive.google.com/..." />
          </div>

          <div>
            <label className="block text-white/80 font-bold mb-2">Inspiration Videos Link</label>
            <p className="text-white/40 text-xs mb-2">Paste a link to a folder or an Instagram reel that shows the VIBE you want.</p>
            <input type="url" value={form.inspiration_link} onChange={e => setForm({...form, inspiration_link: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500" placeholder="https://instagram.com/reel/..." />
          </div>

          <div>
            <label className="block text-white/80 font-bold mb-2">Any specific deadlines or notes?</label>
            <textarea value={form.deadline_note} onChange={e => setForm({...form, deadline_note: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 h-24 resize-none" />
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-lg font-bold mt-4">
            {loading ? "Submitting..." : "Submit Project Brief"}
          </button>
        </form>
      </div>
    </div>
  );
};
